import type { CommonLineChartTooltipProps } from './LineChartTooltip';
import * as d3array from 'd3-array';
import * as d3axis from 'd3-axis';
import * as d3sel from 'd3-selection';
import * as d3scale from 'd3-scale';
import * as d3shape from 'd3-shape';
import type { ScaleLinear, ScaleTime } from 'd3';

import { DefaultDateFormatter, intl, useTrackElementResize } from '../../lib';
import type { RefObject } from 'react';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { D3Axis } from './D3Axis';
import type { LanguageProperty } from '../generated/LanguageProperty';
import { InfoModal } from '../basic';
import { HBox } from '../layout';
import { formatMeasurementByLocale } from '../../util';

export const d3 = { ...d3array, ...d3axis, ...d3sel, ...d3scale, ...d3shape };

export const margin = {
  top: 40,
  right: 30,
  bottom: 30,
  left: 40
};

export type TooltipState = Pick<
  CommonLineChartTooltipProps,
  'data' | 'position' | 'visible' | 'constrainX' | 'hasDuplicateTimestamp'
>;

export const initialTooltipState: TooltipState = {
  data: null,
  visible: false,
  position: {
    x: 0,
    y: 0
  },
  hasDuplicateTimestamp: false
};

const UNKNOWN_INFORMATION = `<${intl('common.unknown')}>`;

export type LineChartProps = {
  /**
   * Title of chart displayed in header
   */
  title: string;
  /**
   * InfoModal popup title displayed next to chart title
   */
  infoModalTitle?: LanguageProperty;
  /**
   * InfoModal popup content displayed next to chart title
   */
  infoModalContent?: LanguageProperty;
  /**
   * A custom component that will be placed in header.
   * Could be used for additional functionality e.g. filtering and sorting
   */
  additionalComponent?: React.JSX.Element;
  initialWidth?: number;
  initialHeight?: number;
  /**
   * If 'true' load mask is placed in front of chart
   */
  loading?: boolean;
  lowReference?: number;
  highReference?: number;
  rangeIndicator?: string;
};

export type DataPointX = {
  x: string;
};

export type DataPointY = {
  value: number;
  unit: string;
  optionalText?: string;
  rangeIndicator?: string;
};

export type SingleLine = {
  firstLine: DataPointY;
};

type DoubleLine = SingleLine & {
  secondLine: DataPointY;
};

export type SingleLineData = DataPointX & {
  y: SingleLine;
};

export type DoubleLineData = DataPointX & {
  y: DoubleLine;
};

/**
 * Returns a formatted string with the value and its unit.
 * If the value is `null` or `undefined`, it returns (translated) "<Unknown>".
 *
 * @param value
 *          value
 * @param unit
 *          unit of the value
 *
 * @returns formatted string with value and unit
 */
export function getValueWithUnit(value?: number | null, unit?: string): string {
  const val = value ?? UNKNOWN_INFORMATION;
  return [String(val), unit].filter(Boolean).join(' ');
}

/**
 * Checks if the provided `data` is of type `DoubleLineData`.
 *
 * @param data
 *          data to check
 *
 * @returns boolean
 */
export function isDoubleLineData(
  data: SingleLineData | DoubleLineData | null
): data is DoubleLineData {
  return !!(data?.y && 'secondLine' in data.y);
}

/**
 * Custom hook to create a scaled x-axis based on the provided `data` and `width`.
 * It uses d3's `scaleUtc` to create a time-based scale.
 * If no valid `x` values within `data` are provided, it defaults to a domain of 0 to 1.
 *
 * @param data
 *          data used for x-axis scale
 * @param width
 *         width of the x-axis
 *
 * @returns scaled x-axis
 */
export function useScaledXAxis(data: SingleLineData[], width: number) {
  return useMemo(() => {
    const extent = d3.extent(data, d => new Date(d.x ?? '').valueOf());

    if (extent[0] == null || extent[1] == null) {
      // no values were given for x-axis, fallback to a domain of 0 to 1
      return d3.scaleUtc([0, 1], [0, width]);
    }

    const sidePadding = (extent[1] - extent[0]) * 0.02;

    return d3.scaleUtc(
      [extent[0] - sidePadding, extent[1] + sidePadding],
      [0, width]
    );
  }, [data, width]);
}

/**
 * Custom hook to create refs for the line chart.
 */
export function useChartRefs() {
  const legendRef = useRef<SVGGElement>(null);
  const dotsGroupRef = useRef<SVGGElement>(null);
  const svgContainerRef = useRef<HTMLDivElement>(null);

  return { legendRef, dotsGroupRef, svgContainerRef };
}

/**
 * Custom hook to track the dimensions of the line chart container.
 * It uses the `useTrackElementResize` hook to detect changes in the container's size.
 *
 * @param svgContainerRef
 *          ref object to the container where the chart is rendered
 * @param initialWidth
 *          initialWidth of the container
 * @param initialHeight
 *          initialHeight of the container
 * @param hasAdditionalComponent
 *          indicates if there is additional component in the header
 * @param hasDoubleLineData
 *          indicates if the chart displays double line data
 *
 * @returns the new width and height of the line chart container
 */
export function useTrackChartDimensions(
  svgContainerRef: RefObject<HTMLDivElement | null>,
  initialWidth: number,
  initialHeight: number,
  hasAdditionalComponent: boolean,
  hasDoubleLineData: boolean
) {
  const [width, setWidth] = useState(initialWidth);
  const [height, setHeight] = useState(initialHeight);
  const bottomSpace = useMemo(() => {
    if (hasAdditionalComponent) {
      return hasDoubleLineData ? 30 : 10;
    } else {
      return hasDoubleLineData ? 10 : 0;
    }
  }, [hasAdditionalComponent, hasDoubleLineData]);

  useTrackElementResize(svgContainerRef, true, () => {
    if (svgContainerRef.current) {
      setWidth(
        svgContainerRef.current.offsetWidth - margin.left - margin.right
      );
      setHeight(
        svgContainerRef.current.offsetHeight -
          margin.top -
          margin.bottom -
          bottomSpace
      );
    }
  });

  return { width, height };
}

/**
 * Hook to handle the tooltip state and show/hide the tooltip based on mouse events.
 *
 * @param data
 *          chart data
 * @param dotsGroupRef
 *          ref object to the svg, in which the data points are rendered
 * @param x
 *          scaled x-axis
 * @param y
 *          scaled y-axis
 * @param width
 *          width of the chart
 *
 * @returns an object containing the tooltip state and handlers to show/hide the tooltip
 */
export function useTooltipHandler(
  data: SingleLineData[] | DoubleLineData[],
  dotsGroupRef: RefObject<SVGGElement | null>,
  x: ScaleTime<number, number>,
  y: ScaleLinear<number, number>,
  width: number
) {
  const [tooltipState, setTooltipState] = useState(initialTooltipState);

  const showTooltip = useCallback((): void => {
    const closestPointAndMousePosition = getClosestPointByRenderedX(
      data,
      dotsGroupRef,
      x
    );

    if (!closestPointAndMousePosition) {
      return;
    }

    const { closestPoint, mousePosition } = closestPointAndMousePosition;
    let valueToDisplay: SingleLineData | DoubleLineData;

    if (isDoubleLineData(closestPoint)) {
      const firstLineCloser = isFirstLineCloser(
        y,
        closestPoint,
        mousePosition[1]
      );

      valueToDisplay = {
        x: closestPoint.x,
        y: {
          firstLine: {
            ...closestPoint.y.firstLine,
            optionalText: firstLineCloser
              ? closestPoint.y.firstLine.optionalText
              : ''
          },
          secondLine: {
            ...closestPoint.y.secondLine,
            optionalText: firstLineCloser
              ? ''
              : closestPoint.y.secondLine.optionalText
          }
        }
      };
    } else {
      valueToDisplay = {
        x: closestPoint.x,
        y: {
          firstLine: closestPoint.y.firstLine
        }
      };
    }

    const closestTimestamp = new Date(closestPoint.x ?? '').valueOf();
    const hasDuplicateTimestamp =
      Number.isFinite(closestTimestamp) &&
      data.filter(d => new Date(d.x ?? '').valueOf() === closestTimestamp)
        .length > 1;

    setTooltipState({
      visible: true,
      data: valueToDisplay,
      position: {
        x: mousePosition[0],
        y: mousePosition[1]
      },
      constrainX: width + margin.left,
      hasDuplicateTimestamp: hasDuplicateTimestamp
    });
  }, [data, dotsGroupRef, x, y, width]);

  // handler for hiding the tooltip
  const hideTooltip = () => {
    setTooltipState(initialTooltipState);
  };

  return { tooltipState, showTooltip, hideTooltip };
}

/**
 * Custom hook to set up the layout effect for showing and hiding a tooltip.
 *
 * @param dotsGroupRef
 *          ref object to the svg, in which the data points are rendered
 * @param showTooltip
 *          handler to show the tooltip
 * @param hideTooltip
 *          handler to hide the tooltip
 */
export function useChartLayoutEffect(
  dotsGroupRef: RefObject<SVGGElement | null>,
  showTooltip: () => void,
  hideTooltip: () => void
) {
  useLayoutEffect(() => {
    if (dotsGroupRef.current) {
      // unfortunately, we need to use `d3.on` so that `d3.mouse` within the showTooltip works
      d3.select(dotsGroupRef.current)
        .on('mousemove', showTooltip)
        .on('mouseout', hideTooltip);
    }
  }, [dotsGroupRef, hideTooltip, showTooltip]);
}

/**
 * Custom hook to manage the loading mask state. The mask is shown at the first render only.
 * We use loading mask to hide the resizing of the chart to the correct dimensions and the axis setup.
 *
 * @returns masked
 * 						boolean indicating whether the loading mask should be shown
 */
export function useShowLoadingMask() {
  const [masked, setMasked] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setMasked(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  return masked;
}

/**
 * Checks if the first line in a DoubleLineData is closer to the mouse position than the second line.
 *
 * @param y
 *          scaled y-axis
 * @param closestPoint
 *          closest point returned from `getClosestPointAndMousePosition`
 * @param mousePositionY
 *          y position of the mouse
 *
 * @returns boolean
 */
function isFirstLineCloser(
  y: ScaleLinear<number, number>,
  closestPoint: DoubleLineData,
  mousePositionY: number
): boolean {
  const firstLineYPosition = y(closestPoint.y.firstLine.value);
  const secondLineYPosition = y(closestPoint.y.secondLine.value);

  // get the distances of the lines relative to Y position of the mouse
  const firstLineDistance = Math.abs(
    (firstLineYPosition as number) - mousePositionY
  );
  const secondLineDistance = Math.abs(
    (secondLineYPosition as number) - mousePositionY
  );

  return firstLineDistance < secondLineDistance;
}

type SharedLineChartSetup = {
  data: SingleLineData[] | DoubleLineData[];
  x: ScaleTime<number, number>;
  y: ScaleLinear<number, number>;
  height: number;
  width: number;
  firstLineLegend?: string;
  legendRef: RefObject<SVGGElement | null>;
};

/**
 * Shared setup of the line charts, for SingleLineChart and DoubleLineChart.
 * This includes things like axes dimensions, labels and legend positioning.
 *
 * @param data
 *          data to be displayed in the chart
 * @param x
 *          scaled x-axis
 * @param y
 *          scaled y-axis
 * @param height
 *          height of the chart
 * @param width
 *          width of the chart
 * @param firstLineLegend
 *          text to be displayed in the legend for the first line
 * @param legendRef
 *          ref object to the legend SVG element
 *
 * @return JSX element containing the shared setup of the line charts
 */
export function SharedLineChartSetup({
  data,
  x,
  y,
  height,
  width,
  firstLineLegend,
  legendRef
}: Readonly<SharedLineChartSetup>): React.JSX.Element {
  return (
    <>
      {firstLineLegend && (
        <g className="legend" ref={legendRef}>
          <rect width={15} height={4} transform="translate(0, -6)" />
          <text className="legend-text" transform="translate(20, 0)">
            {firstLineLegend}
          </text>
        </g>
      )}
      <g transform={`translate(${margin.left},${height - margin.bottom / 2})`}>
        <D3Axis
          // the transform on the g element above already takes care of this, so we're setting 0
          h={0}
          axis={d3
            .axisBottom(x)
            .ticks(Math.floor(width / 120))
            .tickSizeOuter(0)
            .tickFormat(domainValue =>
              DefaultDateFormatter.formatDate(new Date(domainValue.valueOf()))
            )}
          axisType={'x'}
          d3={d3}
        />
        <text className="x-axis-text">{intl('common.date')}</text>
      </g>
      <g transform={`translate(${margin.left},0)`}>
        <D3Axis
          h={height}
          axis={d3
            .axisLeft(y)
            .ticks(Math.floor(height / 40))
            .tickFormat(domainValue =>
              formatMeasurementByLocale(domainValue.valueOf())
            )}
          axisType={'y'}
          d3={d3}
        />
        <text
          className="y-axis-text"
          transform={`rotate(270) translate(-${
            (height + margin.bottom + margin.top) / 2
          }, -55)`}
        >
          {data[0] && data[0].y.firstLine.unit}
        </text>
      </g>
    </>
  );
}

/**
 * Header component for the LineCharts.
 *
 * @param header
 *          text to be displayed in the header
 * @param headerModalTitle
 *          title of the modal
 * @param headerModalContent
 *          content of the modal
 * @param headerFilterComponent
 *          optional additional component displayed along header
 *
 * @return JSX element containing the header with modal and additional component
 */
export function LineChartHeader({
  title,
  infoModalTitle,
  infoModalContent,
  additionalComponent
}: Readonly<
  Omit<LineChartProps, 'initialHeight' | 'initialWidth'>
>): React.JSX.Element {
  return (
    <HBox align="space-between">
      <div className="line-chart-header s-font">
        {title && <h4>{title}</h4>}
        {infoModalTitle && infoModalContent && (
          <InfoModal
            settings={{
              modalTitle: infoModalTitle,
              modalContent: infoModalContent,
              ariaLabel: 'common.moreinfo',
              orderedList: false,
              heading: '' as LanguageProperty,
              showCloseButton: false
            }}
          />
        )}
      </div>
      {additionalComponent && (
        <div className="line-chart-header s-font">{additionalComponent}</div>
      )}
    </HBox>
  );
}

/**
 * Helper function to get the SVG properties for the LineCharts.
 *
 * @param width
 *          width of the chart
 * @param height
 *          height of the chart
 * @param singleLine
 *          indicates whether it's for a single line or not
 *
 * @return object containing the SVG properties
 */
export function getChartSvgProps(
  width: number,
  height: number,
  singleLine?: boolean
) {
  return {
    width: '100%',
    height: 'auto',
    className: `line-chart-svg${singleLine ? ' single-line' : ''}`,
    viewBox: `-30 0 ${width + margin.left + margin.right + 7} ${
      height + margin.bottom
    }`
  };
}

/**
 * Returns a svg compatible icon representing out of range status based on the provided interpretation code.
 *
 * @param interpretationCode
 *          the provided interpretation code
 *
 * @return icon representing out of range status
 */
export function getRangeIndicator(
  interpretationCode: string
): React.JSX.Element {
  if (!interpretationCode) {
    return <></>;
  }
  switch (interpretationCode) {
    case 'H':
      return (
        <tspan
          rotate="45"
          dx="10"
          dy="-4"
          className="graph-tooltip-range-indicator"
        >
          ◤
        </tspan>
      );
    case 'HH':
      return (
        <>
          <tspan
            rotate="45"
            dx="10"
            dy="-6"
            className="graph-tooltip-range-indicator"
          >
            ◤
          </tspan>
          <tspan
            rotate="45"
            dx="-6"
            dy="6"
            className="graph-tooltip-range-indicator"
          >
            ◤
          </tspan>
        </>
      );
    case 'L':
      return (
        <tspan
          rotate="45"
          dx="10"
          dy="-7"
          className="graph-tooltip-range-indicator"
        >
          ◢
        </tspan>
      );
    case 'LL':
      return (
        <>
          <tspan
            rotate="45"
            dx="10"
            dy="-10"
            className="graph-tooltip-range-indicator"
          >
            ◢
          </tspan>
          <tspan
            rotate="45"
            dx="-6"
            dy="6"
            className="graph-tooltip-range-indicator"
          >
            ◢
          </tspan>
        </>
      );
    case 'N':
      return <></>;
    default:
      return <>{' (' + interpretationCode + ')'}</>;
  }
}

/**
 * Renders a shaded area and reference lines to visually indicate a value range on the chart.
 *
 * The shaded area is drawn between the lowReference and highReference values, using the yAxisScale to map these data values to pixel positions. Reference lines are drawn at the low and high boundaries. The color and opacity are set using the primary color from the theme.
 *
 * @param lowReference Lower boundary value for the range (number or undefined).
 * @param highReference Upper boundary value for the range (number or undefined).
 * @param yAxisScale D3 scale function that maps data values to y-axis pixel positions.
 * @param height Height of the chart in pixels.
 * @param width Width of the chart in pixels.
 * @returns JSX element containing the shaded range area and reference lines.
 */
export function renderRangeArea(
  lowReference: number | undefined,
  highReference: number | undefined,
  yAxisScale: ScaleLinear<number, number>,
  height: number,
  width: number
): React.JSX.Element {
  return (
    <g>
      {(lowReference !== undefined || highReference !== undefined) &&
        (() => {
          const yLow =
            lowReference !== undefined ? yAxisScale(lowReference) : null;
          const yHigh =
            highReference !== undefined ? yAxisScale(highReference) : null;

          if (
            lowReference !== undefined &&
            highReference !== undefined &&
            (yLow === undefined || yHigh === undefined)
          ) {
            return null;
          }

          if (
            (lowReference !== undefined && yLow === undefined) ||
            (highReference !== undefined && yHigh === undefined)
          ) {
            return null;
          }

          let yTop: number;
          let yBottom: number;

          if (
            lowReference !== undefined &&
            highReference !== undefined &&
            typeof yLow === 'number' &&
            typeof yHigh === 'number'
          ) {
            yTop = Math.min(yLow, yHigh);
            const originalYBottom = Math.max(yLow, yHigh);
            yBottom = Math.min(originalYBottom, height - margin.bottom);
          } else if (lowReference !== undefined && typeof yLow === 'number') {
            yTop = margin.top;
            yBottom = Math.min(yLow, height - margin.bottom);
          } else {
            yTop = yHigh!;
            yBottom = height - margin.bottom;
          }

          const heightExtension = yBottom >= height - margin.bottom ? 15 : 0;

          return (
            <rect
              x={margin.left}
              y={yTop}
              width={width}
              height={yBottom - yTop + heightExtension}
              fill="var(--primary-color)"
              opacity={0.03}
            />
          );
        })()}
      {lowReference !== undefined && yAxisScale(lowReference) !== undefined && (
        <line
          x1={margin.left}
          x2={margin.left + width}
          y1={yAxisScale(lowReference)!}
          y2={yAxisScale(lowReference)!}
          stroke="var(--primary-color)"
          strokeWidth={1}
        />
      )}
      {highReference !== undefined &&
        yAxisScale(highReference) !== undefined && (
          <line
            x1={margin.left}
            x2={margin.left + width}
            y1={yAxisScale(highReference)!}
            y2={yAxisScale(highReference)!}
            stroke="var(--primary-color)"
            strokeWidth={1}
          />
        )}
    </g>
  );
}

type ClosestTooltipPoint = {
  closestPoint: SingleLineData | DoubleLineData;
  mousePosition: [number, number];
};

function getClosestPointByRenderedX(
  data: SingleLineData[] | DoubleLineData[],
  dotsGroupRef: RefObject<SVGGElement | null>,
  x: ScaleTime<number, number>
): ClosestTooltipPoint | undefined {
  if (!dotsGroupRef.current) {
    return;
  }

  const mousePosition = d3.mouse(dotsGroupRef.current);
  const mouseX = mousePosition[0] - margin.left;

  const closestPoint = data.reduce((prev, curr) => {
    const prevX = x(new Date(prev.x ?? '').valueOf()) as number;
    const currX = x(new Date(curr.x ?? '').valueOf()) as number;

    return Math.abs(prevX - mouseX) < Math.abs(currX - mouseX) ? prev : curr;
  });

  return { closestPoint, mousePosition };
}

export function wrapTextToLines(text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';
  // (font-size 14px, ~7px per char)
  const charsPerLine = Math.floor((maxWidth - 40) / 7);

  words.forEach(word => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length <= charsPerLine) {
      currentLine = testLine;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  });
  if (currentLine) lines.push(currentLine);
  return lines;
}
